/*
 * GEHC CONFIDENTIAL
 *
 * Copyright (c) 2026, 2026, GE HealthCare
 * All Rights Reserved
 *
 * This unpublished material is proprietary to GE HealthCare. The methods and
 * techniques described herein are considered trade secrets and/or
 * confidential. Reproduction or distribution, in whole or in part, is
 * forbidden except by express written permission of GE HealthCare.
 * GE is a trademark of General Electric Company used under trademark license.
 */

import { Injectable } from '@angular/core';
import { Observable, ReplaySubject, Subject } from 'rxjs';
import { Logger, MessageService } from '@core/lib/services';
import { first } from 'rxjs/operators';
import { NotifyService } from '@app/common/notify.service';
import { TranslateService } from '@ngx-translate/core';
import { IntegrationStatusService } from '@app/di/integration-status.service';
import { IntegrationStatus } from '@app/di/integrations/viewer/ev/enterprise-viewer-status.service';

const log = new Logger('DigitalAssistantStatusService');

@Injectable({
  providedIn: 'root'
})
export class DigitalAssistantStatusService {
  private static readonly INTEGRATION_STATUS_DA = 'da';

  // will emit true if the listeners in the system status component are initialized,
  // before that we should not send any status information
  private _systemStatusListening: Subject<boolean> = new ReplaySubject<boolean>(1);
  public systemStatusListening$: Observable<boolean> = this._systemStatusListening.asObservable();

  // will emit exactly one true or false value
  // true - fhircast websocket connection initialized and the digital assistant is started
  // false - fhircast connection failed
  private _useAssistantWithFhircast: Subject<boolean> = new ReplaySubject<boolean>(1);
  public useAssistantWithFhircast$: Observable<boolean> = this._useAssistantWithFhircast.asObservable().pipe(first());

  private launchedExamId: number;

  private _logoutInProgress = false;
  private _integrationStatus = IntegrationStatus.LOADING;
  private _firstInitializationSuccessful = false;
  private _suppressNotifications = true;

  constructor(
    private messageService: MessageService,
    private notifyService: NotifyService,
    private translate: TranslateService,
    private integrationStatusService: IntegrationStatusService
  ) {}

  public setLaunchedExamId(examId: number) {
    this.launchedExamId = examId;
  }

  public getLaunchedExamId() {
    return this.launchedExamId ? this.launchedExamId : 0;
  }

  public systemStatusListening() {
    this._systemStatusListening.next(true);
  }

  public setfirstInitializationSuccessful(status: boolean) {
    this._firstInitializationSuccessful = status;
  }

  public getIntegrationStatus() {
    return this._integrationStatus;
  }

  public isIntegrationReady() {
    switch (this._integrationStatus) {
      case IntegrationStatus.LAST_ACTION_FAILED:
      case IntegrationStatus.SYNC_ERROR:
      case IntegrationStatus.READY:
      case IntegrationStatus.IN_PROGRESS:
        return true;
      default:
        return false;
    }
  }

  public notifyAssistantResponsePending() {
    if (this._suppressNotifications) {
      return;
    }
    return this.notifyService.critical(
      this.translate.instant('di.digitalAssistant.notification.responsePendingTimeout'),
      this.translate.instant('di.dashboard.label.digitalAssistant')
    );
  }

  public isPendingRequest() {
    return this.integrationStatusService.isPendingRequest();
  }

  public assistantLoading() {
    this.changeIntegrationStatus(IntegrationStatus.LOADING, null);
  }

  public assistantInprogress() {
    this.changeIntegrationStatus(IntegrationStatus.IN_PROGRESS, null);
  }

  public readyIntegrationStatus() {
    this.changeIntegrationStatus(IntegrationStatus.READY, null);
  }

  public failedIntegrationStatus(integrationStatus: IntegrationStatus, message: string, isCritical: boolean) {
    this.changeIntegrationStatus(integrationStatus, message, isCritical);
  }

  public healthyIntegrationStatus() {
    this.changeIntegrationStatus(IntegrationStatus.HEALTHY, null);
  }

  private changeIntegrationStatus(integrationStatus: IntegrationStatus, message: string, isCritical = false) {
    this._integrationStatus = integrationStatus;
    this.integrationStatusService.setViewerHealthStatus(integrationStatus);
    switch (integrationStatus) {
      case IntegrationStatus.LOADING:
        console.log('[DA-DEBUG] DigitalAssistantStatusService — status=LOADING, emitting useAssistantWithFhircast$=true');
        log.info('Emitting useAssistantWithFhircast$ = true (LOADING)');
        this._useAssistantWithFhircast.next(true);
        break;
      case IntegrationStatus.IN_PROGRESS:
        break;
      case IntegrationStatus.READY:
        this._firstInitializationSuccessful = true;
        break;
      case IntegrationStatus.HEALTHY:
        if (!this._suppressNotifications) {
          this.notifyService.success(this.translate.instant('di.digitalAssistant.notification.connected'));
        }
        break;
      case IntegrationStatus.FAILED:
        console.warn('[DA-DEBUG] DigitalAssistantStatusService — status=FAILED, emitting useAssistantWithFhircast$=false');
        log.info('Emitting useAssistantWithFhircast$ = false (FAILED)');
        this._useAssistantWithFhircast.next(false);
        log.error('Fhircast integration status: ' + integrationStatus);
        if (!this._suppressNotifications) {
          this.notifyService[isCritical ? 'critical' : 'error'](
            this.translate.instant(message),
            this.translate.instant('di.digitalAssistant.notification.disconnected')
          );
        }
        break;
      case IntegrationStatus.LAST_ACTION_FAILED:
      case IntegrationStatus.SYNC_ERROR:
        log.error('Fhircast integration status: ' + integrationStatus);
        if (!this._suppressNotifications) {
          this.notifyService[isCritical ? 'critical' : 'error'](
            this.translate.instant(message),
            this.translate.instant('di.dashboard.message.lastActionFailed')
          );
        }
        break;
      default:
        log.error('Integration status change not handled.');
    }

    this.messageService.broadcast('integralStatusBoardUpdate', [
      DigitalAssistantStatusService.INTEGRATION_STATUS_DA,
      integrationStatus
    ]);
  }

  get logoutInProgress(): boolean {
    return this._logoutInProgress;
  }

  set logoutInProgress(value: boolean) {
    this._logoutInProgress = value;
  }

  get suppressNotifications(): boolean {
    return this._suppressNotifications;
  }

  set suppressNotifications(value: boolean) {
    this._suppressNotifications = value;
  }

  get firstInitializationSuccessful(): boolean {
    return this._firstInitializationSuccessful;
  }
}
